import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Divider } from 'primereact/divider';
import { useTranslation } from 'react-i18next';

type Party = {
    strana_id: number;
    strana_barva: string;
    strana_zkratka: string;
    pocet_mandatu: number;
}

type PartyListProps = {
    coalitionParties?: Party[];
    oppositionParties?: Party[];
    splitByCoalition?: boolean;
    epText?: boolean;
    hoveredParties?: (string | number)[] | null;
    isSubject?: boolean | null; 
    colors: {
        darkGray: string;
        [key: string]: string;
    };
    twoLists?: boolean;
}

const CircleIcon = ({
    size = 36,
    filled = false
}: {
    size?: number;
    filled?: boolean;
}) => {
    return (
        <svg width={size} height={size} viewBox="0 0 100 100">
            <circle
                cx="50"
                cy="50"
                r="35"
                fill={filled ? '#5d5fef' : 'none'}
                stroke={'#5d5fef'}
                strokeWidth="6"
                opacity={filled ? 1 : 0.5}
            />

            <circle
                cx="75"
                cy="75"
                r="20"
                fill={filled ? 'white' : '#5d5fef'}
                stroke={'#5d5fef'}
                strokeWidth="6"
                opacity={filled ? 0.5 : 1}
            />
        </svg>
    );
};

const PartyList = ({
    coalitionParties = [],
    oppositionParties = [],
    splitByCoalition = true,
    epText,
    hoveredParties,
    isSubject,
    colors,
    twoLists
}: PartyListProps) => {
    const { t } = useTranslation(['parties', 'translation']);
    
    const sortBySeats = (a: Party, b: Party) => b.pocet_mandatu - a.pocet_mandatu;

    const sortedCoalition = useMemo(() => 
        [...coalitionParties].sort(sortBySeats), 
        [coalitionParties]
    );
    
    const sortedOpposition = useMemo(() =>
        [...oppositionParties].sort(sortBySeats),
        [oppositionParties]
    );

    const totalSeats = useMemo(() =>
        [...coalitionParties, ...oppositionParties].reduce((sum, p) => sum + p.pocet_mandatu, 0),
        [coalitionParties, oppositionParties]
    );

    const getIconData = () => {
        if (twoLists) {
            return {
                element: (
                    <CircleIcon filled={!!isSubject} />
                )
            };
        }
    
        return {
            element: (
                <span 
                    className="material-symbols-rounded"
                    style={{ fontSize: '40px' }}
                >
                    thumbs_up_down
                </span>
            )
        };
    };

    const getTitle = () => {
        if (isSubject) {
            if (epText) {
                return t('PartyChart.politicalGroup', { ns: 'parties' });
            } else {
                return t('PartyChart.subject', { ns: 'parties' });
            }
        } else {
            if (epText) {
                return t('PartyChart.nationalParty', { ns: 'parties' });
            } else {
                return t('PartyChart.party', { ns: 'parties' });
            }
        }
    };

    const icon = getIconData();
    const title = getTitle();

    const renderParty = (party: Party) => {
        const stringHoveredParties = hoveredParties?.map(String) || [];
        const isCurrentlyHovered = stringHoveredParties.includes(String(party.strana_id));
        const isAnythingHovered = stringHoveredParties.length > 0;
        
        const shouldBeDimmed = isAnythingHovered && !isCurrentlyHovered;
        const opacitySuffix = '26';

        const partyColor = shouldBeDimmed 
            ? party.strana_barva + opacitySuffix 
            : party.strana_barva;

        const seatShare = totalSeats > 0 ? (party.pocet_mandatu / totalSeats) * 100 : 0;

        return (
            <div key={party.strana_id} className='item-list-party'>
                <div className='item-list-row-top'>
                    {isSubject ? (
                        <Link
                            style={{
                                color: partyColor,
                                transition: 'color 0.2s ease'
                            }}
                            to={`subjekty/${party.strana_id}`}
                        >
                            {<b>{party.strana_zkratka}</b>}
                        </Link>
                    ) : (
                        <span
                            style={{
                                color: partyColor,
                                transition: 'color 0.2s ease',
                                marginBottom: '6px'
                            }}
                        >
                            {<b>{party.strana_zkratka}</b>}
                        </span>
                    )}{' '}
                    <span
                        style={{
                            color: shouldBeDimmed ? colors.darkGray + opacitySuffix : colors.darkGray,
                            transition: 'color 0.2s ease'
                        }}
                    >
                        <span
                            style={{
                                color: partyColor,
                                fontWeight: 600,
                                transition: 'color 0.2s ease',
                                marginBottom: '6px'
                            }}
                        >
                            {party.pocet_mandatu}
                        </span>
                        &nbsp;{t('mandate', {ns: 'translation', count: party.pocet_mandatu })}
                    </span>
                </div>
                <div className='item-list-bar-track'>
                    <div
                        className='item-list-bar-fill'
                        style={{ width: `${seatShare}%`, background: partyColor }}
                    />
                </div>
            </div>
        );
    };

    return (
        <div>
            {splitByCoalition ? (
                <>
                    {sortedCoalition.length > 0 && (
                        <div className='item-list-wrapper'>
                            <div className='item-list'>
                                <div className='item-list-group-header'>
                                    <span className="material-symbols-rounded" style={{ fontSize: '36px', color: '#5d5fef' }}>handshake</span>
                                    <p className='party-group-title' style={{ margin: 0 }}>
                                        {t('PartyChart.coalitionParty', { ns: 'parties' })}
                                    </p>
                                </div>
                                {sortedCoalition.map(renderParty)}
                            </div>
                        </div>
                    )}

                    {sortedCoalition.length > 0 && sortedOpposition.length > 0 && (
                        <Divider layout='horizontal' style={{ margin: '4px 0' }} />
                    )}

                    {sortedOpposition.length > 0 && (
                        <div className='item-list-wrapper'>
                            <div className='item-list'>
                                <div className='item-list-group-header'>
                                    <span className="material-symbols-rounded" style={{ fontSize: '36px', color: '#5d5fef' }}>thumbs_up_down</span>
                                    <p className='party-group-title' style={{ margin: 0 }}>
                                        {t('PartyChart.oppositionParty', { ns: 'parties' })}
                                    </p>
                                </div>
                                {sortedOpposition.map(renderParty)}
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <div className='item-list-wrapper'>
                    <div className='item-list'>
                        <div className='item-list-group-header'>
                            <span className='item-list-group-icon' style={{ display: 'flex', alignItems: 'center' }}>
                                {twoLists ? <CircleIcon size={36} filled={!!isSubject} /> : icon.element}
                            </span>
                            <p className='party-group-title' style={{ margin: 0 }}>
                                {title}
                            </p>
                        </div>
                        {[...coalitionParties, ...oppositionParties].sort(sortBySeats).map(renderParty)}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PartyList;
